/* falseGod — theme runtime: Lenis smooth scroll, header state, hero sound, reveals. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------------------
     Lenis smooth scroll
     ---------------------------------------------------------------------- */
  var lenis = null;

  function startLenis() {
    if (lenis || reduceMotion || typeof window.Lenis !== 'function') return;

    lenis = new window.Lenis({
      duration: 1.1,
      easing: function (t) {
        return Math.min(1, 1.001 - Math.pow(2, -10 * t));
      },
      smoothWheel: true,
      // Native momentum on touch feels better than an emulated one.
      syncTouch: false,
      touchMultiplier: 1.6,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    window.fgLenis = lenis;

    // In-page anchors should ride the same easing.
    document.addEventListener('click', function (event) {
      var link = event.target.closest('a[href^="#"]');
      if (!link) return;
      var id = link.getAttribute('href');
      if (!id || id === '#' || link.hasAttribute('data-lenis-ignore')) return;
      var target = document.querySelector(id);
      if (!target) return;
      event.preventDefault();
      lenis.scrollTo(target, { offset: -100 });
    });
  }

  /* Drawers and modals must not scroll the page behind them. */
  function lockScroll(locked) {
    if (!lenis) return;
    locked ? lenis.stop() : lenis.start();
  }
  window.fgLockScroll = lockScroll;

  /* ----------------------------------------------------------------------
     Header — scrolled state + mobile drawer
     ---------------------------------------------------------------------- */
  function initHeader(root) {
    if (!root || root.dataset.fgHeaderReady === 'true') return;
    root.dataset.fgHeaderReady = 'true';

    var burger = root.querySelector('[data-fg-burger]');

    function onScroll() {
      root.classList.toggle('is-scrolled', window.scrollY > 24);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    // Marks the header once the hero has scrolled by. Nothing styles this yet —
    // it is the hook for "header hidden after hero" when that lands.
    var hero = document.querySelector('[data-fg-hero]');
    if (hero && 'IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            root.classList.toggle('is-past-hero', !entry.isIntersecting);
          });
        },
        { rootMargin: '-40% 0px 0px 0px' }
      ).observe(hero);
    }

    function setOpen(open) {
      root.classList.toggle('is-open', open);
      if (burger) burger.setAttribute('aria-expanded', String(open));
      lockScroll(open);
      document.documentElement.classList.toggle('fg-menu-open', open);
    }

    if (burger) {
      burger.addEventListener('click', function () {
        setOpen(!root.classList.contains('is-open'));
      });
    }

    root.querySelectorAll('[data-fg-drawer-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        setOpen(false);
        if (burger) burger.focus();
      });
    });

    // Following a link should not leave the overlay covering the next page.
    root.querySelectorAll('[data-fg-drawer] a').forEach(function (link) {
      link.addEventListener('click', function () {
        setOpen(false);
      });
    });

    // Close the drawer on Escape.
    root.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !root.classList.contains('is-open')) return;
      setOpen(false);
      if (burger) burger.focus();
    });
  }

  /* ----------------------------------------------------------------------
     Hero video — sound toggle, and pause while offscreen
     ---------------------------------------------------------------------- */
  function initHero(root) {
    if (!root || root.dataset.fgHeroReady === 'true') return;
    root.dataset.fgHeroReady = 'true';

    var video = root.querySelector('video');
    if (!video) return;

    // Autoplay is only permitted while muted; play() can still reject.
    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});

    var toggle = root.querySelector('[data-fg-sound]');
    if (toggle) {
      toggle.addEventListener('click', function () {
        video.muted = !video.muted;
        if (!video.muted) video.play().catch(function () {});
        toggle.setAttribute('aria-pressed', String(!video.muted));
        toggle.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
        toggle.querySelectorAll('[data-state]').forEach(function (icon) {
          icon.hidden = icon.dataset.state !== (video.muted ? 'muted' : 'unmuted');
        });
      });
    }

    // Don't decode frames the visitor cannot see.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              video.play().catch(function () {});
            } else {
              video.pause();
            }
          });
        },
        { threshold: 0.15 }
      ).observe(root);
    }
  }

  /* ----------------------------------------------------------------------
     Marquee — duplicate the group so the -50% loop is seamless
     ---------------------------------------------------------------------- */
  function initMarquee(root) {
    if (!root || root.dataset.fgMarqueeReady === 'true') return;
    root.dataset.fgMarqueeReady = 'true';

    var track = root.querySelector('.fg-marquee__track');
    var group = track && track.querySelector('.fg-marquee__group');
    if (!track || !group) return;
    if (track.children.length < 2) track.appendChild(group.cloneNode(true));
  }

  /* ----------------------------------------------------------------------
     Product vitrine — gallery paging, thumbnails, keyboard and swipe
     ---------------------------------------------------------------------- */
  function initVitrine(root) {
    if (!root || root.dataset.fgVitrineReady === 'true') return;
    root.dataset.fgVitrineReady = 'true';

    var shots = Array.prototype.slice.call(root.querySelectorAll('[data-fg-slide]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-fg-thumb]'));
    if (shots.length === 0) return;

    var index = 0;

    function show(next) {
      index = (next + shots.length) % shots.length;
      shots.forEach(function (shot, i) {
        shot.classList.toggle('is-active', i === index);
      });
      thumbs.forEach(function (thumb, i) {
        var on = i === index;
        thumb.classList.toggle('is-active', on);
        thumb.setAttribute('aria-selected', String(on));
      });
    }

    var prev = root.querySelector('[data-fg-prev]');
    var next = root.querySelector('[data-fg-next]');
    if (prev) prev.addEventListener('click', function () { show(index - 1); });
    if (next) next.addEventListener('click', function () { show(index + 1); });

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { show(i); });
    });

    // A single image needs no controls.
    if (shots.length < 2) {
      [prev, next, root.querySelector('[data-fg-thumbs]')].forEach(function (el) {
        if (el) el.hidden = true;
      });
      return;
    }

    var stage = root.querySelector('.fg-stage');
    if (stage) {
      stage.setAttribute('tabindex', '0');
      stage.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowLeft') { event.preventDefault(); show(index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); show(index + 1); }
      });

      var startX = null;
      stage.addEventListener('touchstart', function (e) {
        startX = e.changedTouches[0].clientX;
      }, { passive: true });
      stage.addEventListener('touchend', function (e) {
        if (startX === null) return;
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 45) show(dx < 0 ? index + 1 : index - 1);
        startX = null;
      }, { passive: true });
    }
  }

  /* ----------------------------------------------------------------------
     Size pills -> the hidden [name=id] that <product-form> submits
     ---------------------------------------------------------------------- */
  function initVariantPills(root) {
    if (!root || root.dataset.fgVariantsReady === 'true') return;
    root.dataset.fgVariantsReady = 'true';

    var input = root.querySelector('[data-fg-variant-input]');
    var radios = Array.prototype.slice.call(root.querySelectorAll('[data-fg-variant]'));
    if (!input || !radios.length) return;

    var button = root.querySelector('.fg-buy__add');
    var label = button && button.querySelector('span');
    var soldOut = (window.variantStrings && window.variantStrings.soldOut) || 'Sold out';
    var addLabel = label ? label.textContent.trim() : '';

    function sync() {
      var checked = radios.filter(function (r) { return r.checked; })[0];
      if (!checked) return;
      input.value = checked.value;

      // Keep the button honest about the selected variant. Sold-out radios are
      // rendered disabled, so a checked one is by definition purchasable.
      if (!button) return;
      var available = !checked.disabled;
      button.disabled = !available;
      if (label && addLabel) label.textContent = available ? addLabel : soldOut;
    }

    radios.forEach(function (radio) {
      radio.addEventListener('change', sync);
    });
    sync();
  }

  /* ----------------------------------------------------------------------
     Size guide dialog
     ---------------------------------------------------------------------- */
  function initSizeGuide(root) {
    if (!root || root.dataset.fgGuideReady === 'true') return;
    root.dataset.fgGuideReady = 'true';

    var dialog = root.querySelector('[data-fg-guide]');
    var open = root.querySelector('[data-fg-guide-open]');
    if (!dialog || !open) return;

    var supportsModal = typeof dialog.showModal === 'function';

    open.addEventListener('click', function () {
      if (supportsModal) {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
      lockScroll(true);
    });

    function close() {
      if (supportsModal && dialog.open) {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
      }
    }

    root.querySelectorAll('[data-fg-guide-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });

    // Click on the backdrop — that is, outside the panel — dismisses it.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) close();
    });

    // <dialog> closes itself on Escape; catch every path back to the page.
    dialog.addEventListener('close', function () {
      lockScroll(false);
      open.focus();
    });
  }

  /* ----------------------------------------------------------------------
     Product tabs
     ---------------------------------------------------------------------- */
  function initTabs(root) {
    if (!root || root.dataset.fgTabsReady === 'true') return;
    root.dataset.fgTabsReady = 'true';

    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-fg-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-fg-panel]'));
    if (!tabs.length) return;

    function select(i) {
      tabs.forEach(function (tab, n) {
        var on = n === i;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', String(on));
      });
      panels.forEach(function (panel, n) {
        panel.hidden = n !== i;
        panel.classList.toggle('is-active', n === i);
      });
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { select(i); });
      tab.addEventListener('keydown', function (event) {
        var step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        event.preventDefault();
        var target = (i + step + tabs.length) % tabs.length;
        tabs[target].focus();
        select(target);
      });
    });
  }

  /* ----------------------------------------------------------------------
     Footer groups — collapsed on mobile, always open on desktop
     ---------------------------------------------------------------------- */
  function initFooterGroups() {
    var groups = Array.prototype.slice.call(
      document.querySelectorAll('[data-fg-footer-group]')
    );
    if (!groups.length) return;

    var mq = window.matchMedia('(max-width: 750px)');

    function sync(event) {
      // Rendered with `open` so the content is always there without JS; only
      // the initial state is managed here, and only when the breakpoint is
      // crossed — otherwise this would fight the visitor's own toggling.
      groups.forEach(function (group) {
        group.open = !event.matches;
      });
    }

    sync(mq);
    if (mq.addEventListener) {
      mq.addEventListener('change', sync);
    } else if (mq.addListener) {
      mq.addListener(sync);
    }
  }

  /* ----------------------------------------------------------------------
     Reveal on scroll
     ---------------------------------------------------------------------- */
  var revealObserver = null;

  function initReveals(scope) {
    var targets = (scope || document).querySelectorAll('.fg-reveal:not(.is-in)');
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
      );
    }
    targets.forEach(function (el) {
      revealObserver.observe(el);
    });
  }

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */
  function boot(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-fg-header]').forEach(initHeader);
    root.querySelectorAll('[data-fg-hero]').forEach(initHero);
    root.querySelectorAll('[data-fg-marquee]').forEach(initMarquee);
    root.querySelectorAll('[data-fg-vitrine]').forEach(initVitrine);
    root.querySelectorAll('[data-fg-vitrine]').forEach(initVariantPills);
    root.querySelectorAll('[data-fg-vitrine]').forEach(initSizeGuide);
    root.querySelectorAll('[data-fg-tabs]').forEach(initTabs);
    initFooterGroups();
    initReveals(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      startLenis();
      boot(document);
    });
  } else {
    startLenis();
    boot(document);
  }

  /* Theme editor: sections are swapped in without a page load. */
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
    if (lenis) lenis.resize();
  });
  document.addEventListener('shopify:section:select', function () {
    if (lenis) lenis.stop();
  });
  document.addEventListener('shopify:section:deselect', function () {
    if (lenis) lenis.start();
  });
})();
